import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';
import TextEditor from '../components/TextEditor';

function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    featured_image: '',
    images: [], // Array to store multiple images
    status: 'planned',
    progress: 0,
    start_date: '',
    end_date: '',
    beneficiaries: '',
    outcomes: ''
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchProjects();
  }, [navigate, search, statusFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = getAccessToken();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      params.append('page', String(currentPage));
      params.append('limit', '50');

      const response = await axios.get(`/api/projects?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setProjects(response.data.projects);
      setPagination(response.data.pagination);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      e.target.value = '';
      setImagePreview(null);
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use filename directly from response
      const filename = response.data.filename;
      
      if (!filename) {
        throw new Error('No filename returned from server');
      }
      
      setFormData(prev => ({ ...prev, featured_image: filename }));
      e.target.value = '';
    } catch (error) {
      alert('Failed to upload image');
      console.error('Error uploading image:', error);
      setImagePreview(null);
      e.target.value = '';
    } finally {
      setUploadingImage(false);
    }
  };

  const handleMultipleImagesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    // Validate file sizes
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Image size must be less than 5MB`);
        e.target.value = '';
        return;
      }
    }

    setUploadingImage(true);
    try {
      const token = getAccessToken();
      const uploadedImages = [];

      // Upload files one by one
      for (const file of files) {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);

        const response = await axios.post('/api/upload', uploadFormData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        const filename = response.data.filename;
        if (filename) {
          uploadedImages.push({ filename, preview: URL.createObjectURL(file) });
        }
      }

      // Add uploaded images to formData
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));

      e.target.value = '';
    } catch (error) {
      alert('Failed to upload images');
      console.error('Error uploading images:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      short_description: '',
      featured_image: '',
      images: [],
      status: 'planned',
      progress: 0,
      start_date: '',
      end_date: '',
      beneficiaries: '',
      outcomes: ''
    });
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowAddModal(true);
  };

  const handleEdit = async (project) => {
    setEditingProject(project);
    
    // Fetch project with images
    try {
      const token = getAccessToken();
      const response = await axios.get(`/api/projects/${project.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const projectData = response.data;
      setFormData({
        title: projectData.title || '',
        description: projectData.description || '',
        short_description: projectData.short_description || '',
        featured_image: projectData.featured_image || '',
        images: projectData.images || [],
        status: projectData.status || 'planned',
        progress: projectData.progress || 0,
        start_date: projectData.start_date ? projectData.start_date.split('T')[0] : '',
        end_date: projectData.end_date ? projectData.end_date.split('T')[0] : '',
        beneficiaries: projectData.beneficiaries || '',
        outcomes: projectData.outcomes || ''
      });
    } catch (error) {
      console.error('Error fetching project:', error);
      // Fallback to basic project data
      setFormData({
        title: project.title || '',
        description: project.description || '',
        short_description: project.short_description || '',
        featured_image: project.featured_image || '',
        images: [],
        status: project.status || 'planned',
        progress: project.progress || 0,
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
        beneficiaries: project.beneficiaries || '',
        outcomes: project.outcomes || ''
      });
    }
    
    setFormError('');
    setImagePreview(null);
    setUploadingImage(false);
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const token = getAccessToken();
      const url = editingProject ? `/api/projects/${editingProject.id}` : '/api/projects';
      const method = editingProject ? 'put' : 'post';

      // Prepare data without images array (images will be added separately)
      const { images, ...projectData } = formData;
      
      // Create/update project first
      const response = await axios[method](url, projectData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const projectId = editingProject ? editingProject.id : response.data.id;

      // Add new images if any were uploaded (only new ones, not existing ones from DB)
      if (images && images.length > 0) {
        // Filter out existing images (those with an 'id' property are from DB)
        const newImages = images.filter(img => !img.id);
        
        if (newImages.length > 0) {
          // Get current image count to set display_order correctly
          const currentImagesResponse = await axios.get(`/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const currentImageCount = currentImagesResponse.data.images?.length || 0;
          
          for (let i = 0; i < newImages.length; i++) {
            let imageFilename;
            if (typeof newImages[i] === 'string') {
              imageFilename = newImages[i];
            } else if (newImages[i].filename) {
              imageFilename = newImages[i].filename;
            } else if (newImages[i].image) {
              // This shouldn't happen for new images, but handle it just in case
              imageFilename = newImages[i].image;
            } else {
              continue; // Skip invalid images
            }
            
            await axios.post(
              `/api/projects/${projectId}/images`,
              { image: imageFilename, display_order: currentImageCount + i },
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );
          }
        }
      }

      setShowAddModal(false);
      setShowEditModal(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        setFormError(error.response?.data?.error || 'Failed to save project');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      const token = getAccessToken();
      await axios.delete(`/api/projects/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        alert('Failed to delete project');
      }
    }
  };


  const getStatusBadge = (status) => {
    const badges = {
      ongoing: { text: 'Ongoing', class: 'bg-success' },
      completed: { text: 'Completed', class: 'bg-primary' },
      planned: { text: 'Planned', class: 'bg-warning' }
    };
    const badge = badges[status] || badges.planned;
    return <span className={`badge ${badge.class} text-white`}>{badge.text}</span>;
  };

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Projects Management</h2>
          <button className="btn btn-primary" onClick={handleAdd}>
            <i className="bi bi-plus-circle me-2"></i>Add New Project
          </button>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="planned">Planned</option>
            </select>
          </div>
        </div>

        {/* Projects Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Start Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => {
                  const imageUrl = project.featured_image
                    ? (project.featured_image.startsWith('/uploads/')
                        ? project.featured_image
                        : `/uploads/${project.featured_image}`)
                    : '/img/Gallery/opd1.jpg';

                  return (
                    <tr key={project.id}>
                      <td>
                        <img
                          src={imageUrl}
                          alt={project.title}
                          style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                          onError={(e) => {
                            e.target.src = '/img/Gallery/opd1.jpg';
                          }}
                        />
                      </td>
                      <td>{project.title}</td>
                      <td>{getStatusBadge(project.status)}</td>
                      <td>
                        {project.status === 'ongoing' && project.progress !== null ? (
                          <span>{project.progress}%</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => handleEdit(project)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-outline-danger" onClick={() => handleDelete(project.id)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
            {pagination.pages > 1 && (
              <div className="mt-3 d-flex justify-content-between align-items-center">
                <p className="text-muted mb-0">
                  Page {pagination.page} of {pagination.pages} (Total: {pagination.total})
                </p>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={loading || (pagination.page || 1) <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                    disabled={loading || (pagination.page || 1) >= (pagination.pages || 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add/Edit Modal */}
        {(showAddModal || showEditModal) && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-scrollable modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingProject ? 'Edit Project' : 'Add New Project'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setEditingProject(null);
                    }}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {formError && (
                      <div className="alert alert-danger" role="alert">
                        {formError}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Short Description</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.short_description}
                        onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                        placeholder="Brief description (2-3 lines)"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Full Description *</label>
                      <TextEditor
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Featured Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleFeaturedImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {/* Show preview of selected file (before or after upload) */}
                      {imagePreview ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">
                            {uploadingImage ? 'Uploading...' : 'Selected image:'}
                          </p>
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            style={{ width: '200px', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {formData.featured_image && !uploadingImage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-danger ms-2 mt-2"
                              onClick={() => {
                                setFormData({ ...formData, featured_image: '' });
                                setImagePreview(null);
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ) : formData.featured_image ? (
                        <div className="mt-2">
                          <p className="text-muted small mb-1">Current image:</p>
                          <img
                            src={formData.featured_image.startsWith('/uploads/') ? formData.featured_image : `/uploads/${formData.featured_image}`}
                            alt="Featured"
                            style={{ width: '200px', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error loading image:', formData.featured_image);
                              e.target.src = '/img/Gallery/opd1.jpg';
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-danger ms-2 mt-2"
                            onClick={() => {
                              setFormData({ ...formData, featured_image: '' });
                              setImagePreview(null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Additional Images (for slider)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        multiple
                        onChange={handleMultipleImagesUpload}
                        disabled={uploadingImage}
                      />
                      <small className="text-muted d-block mt-1">You can select multiple images at once. They will appear in the project slider.</small>
                      {uploadingImage && <div className="text-muted mt-2">Uploading...</div>}
                      {formData.images && formData.images.length > 0 && (
                        <div className="mt-3">
                          <p className="small text-muted mb-2">Uploaded Images ({formData.images.length}):</p>
                          <div className="d-flex flex-wrap gap-2">
                            {formData.images.map((img, index) => {
                              // Handle different image formats:
                              // - String: direct filename
                              // - Object with filename: new upload
                              // - Object with image: existing from DB
                              let imgUrl;
                              let preview;
                              
                              if (typeof img === 'string') {
                                imgUrl = img.startsWith('/uploads/') ? img : `/uploads/${img}`;
                                preview = imgUrl;
                              } else if (img.filename) {
                                // New upload with filename property
                                imgUrl = img.filename.startsWith('/uploads/') ? img.filename : `/uploads/${img.filename}`;
                                preview = img.preview || imgUrl;
                              } else if (img.image) {
                                // Existing image from database
                                imgUrl = img.image.startsWith('/uploads/') ? img.image : `/uploads/${img.image}`;
                                preview = imgUrl;
                              } else {
                                // Fallback
                                imgUrl = '/img/Gallery/opd1.jpg';
                                preview = imgUrl;
                              }
                              
                              return (
                                <div key={index} className="position-relative" style={{ width: '120px', height: '90px' }}>
                                  <img
                                    src={preview}
                                    alt={`Image ${index + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                    onError={(e) => {
                                      e.target.src = '/img/Gallery/opd1.jpg';
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                    style={{ transform: 'translate(50%, -50%)', padding: '2px 6px' }}
                                    onClick={() => removeImage(index)}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Status *</label>
                        <select
                          className="form-select"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          required
                        >
                          <option value="planned">Planned</option>
                          <option value="ongoing">Ongoing</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Progress (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          max="100"
                          value={formData.progress}
                          onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })}
                          disabled={formData.status !== 'ongoing'}
                        />
                        <small className="text-muted">Only for ongoing projects</small>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Beneficiaries</label>
                      <TextEditor
                        value={formData.beneficiaries}
                        onChange={(value) => setFormData({ ...formData, beneficiaries: value })}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Outcomes & Impact</label>
                      <TextEditor
                        value={formData.outcomes}
                        onChange={(value) => setFormData({ ...formData, outcomes: value })}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddModal(false);
                        setShowEditModal(false);
                        setEditingProject(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={formLoading}>
                      {formLoading ? 'Saving...' : editingProject ? 'Update Project' : 'Create Project'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </AdminLayout>
  );
}

export default AdminProjects;
